/**
 * Parallel LMS Staff Management System — permission-aware tab hub
 * (Phase 10A frontend, plan §8 "Frontend Architecture"; tabs 3-6 filled in
 * during Phase 10B).
 *
 * On mount this reads GET /api/lms/admin/my_permissions/ (returns one row per
 * LmsStaffPermission.TAB_CHOICES entry; superusers implicitly get
 * can_view/can_edit true on every tab) and renders shadcn `Tabs` containing
 * ONLY the tabs whose `can_view` is true. Each tab's `can_edit` is passed down
 * to its child component so the child can disable its mutating controls — the
 * backend independently enforces the same rule via HasLmsTabPermission, so this
 * is UX affordance, never the security boundary.
 *
 * PHASE 10B ADDITION — cross-tab loan navigation
 * ----------------------------------------------
 * Tabs 4/5/6 render loan IDs that the plan requires to hyperlink to the loan
 * detail/edit view, but this feature has no per-loan route (it lives inside one
 * AdminDashboard `activeView`). So the hub now owns two extra pieces of state:
 *
 *   * `activeTab`   — the `Tabs` value is CONTROLLED (was `defaultValue`) so a
 *                     link can switch tabs programmatically.
 *   * `pendingLoan` — {tab, loanId, nonce}: the loan to hand the target tab.
 *
 * Both are published to descendants via `LmsStaffNavProvider` as
 * `{openLoan, canOpen}` and consumed by `<LoanIdLink>` (lmsStaffNav.tsx). Each
 * tab body is keyed on the nonce so clicking a second loan ID for the tab you
 * are already on remounts that tab with the new `initialLoanId` instead of
 * silently doing nothing (Radix unmounts inactive TabsContent, so a plain prop
 * change would otherwise be missed).
 *
 * This is the only change to the Phase 10A structure — additive, and the tab
 * definitions stay declarative.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldAlert, LayoutDashboard } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

import lmsStaffService, {
    LmsTabKey,
    MyLmsPermission,
    getLmsApiErrorMessage,
} from "./lmsStaffApi";
import LoanProfilePage from "./LoanProfilePage";
import MandateEditPanel from "./MandateEditPanel";
import AddPresentationPanel from "./AddPresentationPanel";
import OverviewSummary from "./OverviewSummary";
import OverdueSplitView from "./OverdueSplitView";
import CalendarOverviewTab from "./CalendarOverviewTab";
import { LmsStaffNavProvider, LoanNavTarget } from "./lmsStaffNav";

/** Props every tab body receives. `canEdit` is that tab's own can_edit. */
interface TabBodyProps {
    canEdit: boolean;
    /**
     * Set only when the user arrived here by clicking a loan ID on another tab;
     * undefined during ordinary tab switching.
     */
    initialLoanId?: string | null;
}

interface TabDef {
    key: LmsTabKey;
    label: string;
    render: (props: TabBodyProps) => React.ReactNode;
}

/**
 * Tab order matches LmsStaffPermission.TAB_CHOICES (backend/lms/models.py) so
 * the UI order and the permission rows always line up.
 */
const TAB_DEFS: TabDef[] = [
    {
        key: "loan_profile",
        label: "Loan Profile",
        // Task 1 is fully read-only — no can_edit gating needed on its content.
        render: ({ initialLoanId }) => <LoanProfilePage initialLoanId={initialLoanId} />,
    },
    {
        key: "mandate_pg",
        label: "Mandate & Payments",
        render: ({ canEdit, initialLoanId }) => (
            <MandateEditPanel canEdit={canEdit} initialLoanId={initialLoanId} />
        ),
    },
    {
        key: "presentation_collection",
        label: "Raise Presentation",
        render: ({ canEdit, initialLoanId }) => (
            <AddPresentationPanel canEdit={canEdit} initialLoanId={initialLoanId} />
        ),
    },
    {
        key: "overview_disbursement",
        label: "System Overview",
        render: ({ canEdit }) => <OverviewSummary canEdit={canEdit} />,
    },
    {
        key: "overview_overdue_split",
        label: "Overdue Split",
        render: ({ canEdit }) => <OverdueSplitView canEdit={canEdit} />,
    },
    {
        key: "calendar",
        label: "Calendar",
        render: ({ canEdit }) => <CalendarOverviewTab canEdit={canEdit} />,
    },
];

/** The loan handed to a tab by a cross-tab link. */
interface PendingLoan {
    tab: LoanNavTarget;
    loanId: string;
    /** Bumped on every jump so a repeat click remounts the target tab body. */
    nonce: number;
}

const LmsStaffOpsHub: React.FC = () => {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ["lms-staff-my-permissions"],
        queryFn: async () => {
            const response = await lmsStaffService.getMyPermissions();
            return response.data as MyLmsPermission[];
        },
    });

    const permissionByTab = React.useMemo(() => {
        const map = new Map<LmsTabKey, MyLmsPermission>();
        (data ?? []).forEach((row) => map.set(row.tab, row));
        return map;
    }, [data]);

    const visibleTabs = React.useMemo(
        () => TAB_DEFS.filter((tab) => permissionByTab.get(tab.key)?.can_view === true),
        [permissionByTab]
    );

    const [activeTab, setActiveTab] = React.useState<LmsTabKey | null>(null);
    const [pendingLoan, setPendingLoan] = React.useState<PendingLoan | null>(null);

    // The permission call resolves after first render, so the initial active tab
    // is adopted once (and only once) the visible set is known.
    React.useEffect(() => {
        if (activeTab === null && visibleTabs.length > 0) {
            setActiveTab(visibleTabs[0].key);
        }
    }, [activeTab, visibleTabs]);

    const canOpen = React.useCallback(
        (tab: LoanNavTarget) => permissionByTab.get(tab)?.can_view === true,
        [permissionByTab]
    );

    const openLoan = React.useCallback(
        (loanId: string, tab: LoanNavTarget) => {
            if (!canOpen(tab)) return;
            setPendingLoan((current) => ({
                tab,
                loanId,
                nonce: (current?.nonce ?? 0) + 1,
            }));
            setActiveTab(tab);
        },
        [canOpen]
    );

    const navValue = React.useMemo(() => ({ openLoan, canOpen }), [openLoan, canOpen]);

    // Loading — a lightweight inline state rather than FullScreenLoader, which
    // is a fixed full-viewport overlay built for the customer journey's blocking
    // waits and would obscure the surrounding admin dashboard chrome here.
    if (isLoading) {
        return (
            <div className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white py-24">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
                    <p className="text-sm text-gray-500">
                        Loading your LMS staff permissions...
                    </p>
                </div>
            </div>
        );
    }

    if (isError) {
        const message = getLmsApiErrorMessage(
            error,
            "Unable to load your LMS staff permissions."
        );
        return (
            <div className="w-full rounded-xl border border-red-100 bg-red-50 p-6">
                <div className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-5 w-5 text-red-600" />
                    <div className="space-y-3">
                        <p className="text-sm font-medium text-red-700">{message}</p>
                        <Button variant="outline" size="sm" onClick={() => refetch()}>
                            Retry
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (visibleTabs.length === 0) {
        return (
            <div className="w-full rounded-xl border border-gray-200 bg-white p-12 text-center">
                <ShieldAlert className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-3 text-sm font-medium text-gray-700">
                    No LMS staff tabs are available to your account.
                </p>
                <p className="mt-1 text-sm text-gray-500">
                    Ask a superuser to grant you view access to at least one LMS
                    staff tab.
                </p>
            </div>
        );
    }

    return (
        <LmsStaffNavProvider value={navValue}>
            <div className="w-full space-y-4">
                <div>
                    <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                        <LayoutDashboard className="h-6 w-6 text-intellend-green" />
                        LMS Staff Operations
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Showing only the tabs your account has view permission for.
                    </p>
                </div>

                <Tabs
                    value={activeTab ?? visibleTabs[0].key}
                    onValueChange={(value) => setActiveTab(value as LmsTabKey)}
                    className="w-full"
                >
                    <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                        {visibleTabs.map((tab) => (
                            <TabsTrigger key={tab.key} value={tab.key}>
                                {tab.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {visibleTabs.map((tab) => {
                        const handoff =
                            pendingLoan && pendingLoan.tab === tab.key ? pendingLoan : null;
                        return (
                            <TabsContent key={tab.key} value={tab.key} className="mt-4">
                                {/* Keyed on the nonce so a repeat jump remounts. */}
                                <div key={handoff ? `loan-${handoff.nonce}` : "default"}>
                                    {tab.render({
                                        canEdit:
                                            permissionByTab.get(tab.key)?.can_edit === true,
                                        initialLoanId: handoff?.loanId ?? null,
                                    })}
                                </div>
                            </TabsContent>
                        );
                    })}
                </Tabs>
            </div>
        </LmsStaffNavProvider>
    );
};

export default LmsStaffOpsHub;
